import { useState, useRef, useCallback, useEffect } from 'react'
import ReactCrop, { 
  type Crop, 
  type PixelCrop,
  centerCrop,
  makeAspectCrop
} from 'react-image-crop'
import { motion } from 'framer-motion'
import { 
  Upload, 
  Download, 
  RotateCw, 
  Square, 
  Circle, 
  Smartphone,
  Monitor,
  Users,
  Gamepad2,
  RefreshCw,
  Crop as CropIcon,
  Sliders,
  Sun,
  Contrast,
  Palette,
  Eye,
  FileImage,
  Maximize
} from 'lucide-react'
import { useIntl } from 'react-intl'
import 'react-image-crop/dist/ReactCrop.css'

interface ImageCropperProps {
  onCropComplete?: (croppedImageUrl: string) => void
}

interface PresetSize {
  name: string
  size: string
  aspect: number | undefined
  icon: React.ReactNode
  width: number
  height: number
}

const ImageCropper: React.FC<ImageCropperProps> = ({ onCropComplete }) => {
  const intl = useIntl()
  const [imgSrc, setImgSrc] = useState<string>('')
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [scale, setScale] = useState(1)
  const [rotate, setRotate] = useState(0)
  const [aspect, setAspect] = useState<number | undefined>(1)
  const [isCircular, setIsCircular] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<string>('square')
  const [targetWidth, setTargetWidth] = useState<number>(400)
  const [targetHeight, setTargetHeight] = useState<number>(400)
  
  // Advanced editing states
  const [brightness, setBrightness] = useState(100)
  const [contrast, setContrast] = useState(100)
  const [saturation, setSaturation] = useState(100)
  const [blur, setBlur] = useState(0)
  const [selectedFilter, setSelectedFilter] = useState('none')
  const [showAdvancedControls, setShowAdvancedControls] = useState(false)
  
  // Export settings
  const [exportFormat, setExportFormat] = useState<'png' | 'jpeg' | 'webp'>('png')
  const [exportQuality, setExportQuality] = useState(0.9)
  const [exportSize, setExportSize] = useState<'original' | 'small' | 'medium' | 'large'>('original')
  
  // UI states
  const [isDragOver, setIsDragOver] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadSuccess, setDownloadSuccess] = useState(false)
  
  const imgRef = useRef<HTMLImageElement>(null)
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const hiddenAnchorRef = useRef<HTMLAnchorElement>(null)
  const blobUrlRef = useRef('')

  const presetSizes: PresetSize[] = [
    { name: intl.formatMessage({ id: 'imageCropper.presets.square' }), size: '1:1', aspect: 1, icon: <Square className="h-4 w-4" />, width: 400, height: 400 },
    { name: intl.formatMessage({ id: 'imageCropper.presets.circular' }), size: '1:1', aspect: 1, icon: <Circle className="h-4 w-4" />, width: 400, height: 400 },
    { name: intl.formatMessage({ id: 'imageCropper.presets.discord' }), size: '128x128', aspect: 1, icon: <Users className="h-4 w-4" />, width: 128, height: 128 },
    { name: intl.formatMessage({ id: 'imageCropper.presets.twitter' }), size: '400x400', aspect: 1, icon: <Monitor className="h-4 w-4" />, width: 400, height: 400 },
    { name: intl.formatMessage({ id: 'imageCropper.presets.linkedin' }), size: '300x300', aspect: 1, icon: <Monitor className="h-4 w-4" />, width: 300, height: 300 },
    { name: intl.formatMessage({ id: 'imageCropper.presets.steam' }), size: '184x184', aspect: 1, icon: <Gamepad2 className="h-4 w-4" />, width: 184, height: 184 },
    { name: intl.formatMessage({ id: 'imageCropper.presets.instagram' }), size: '320x320', aspect: 1, icon: <Smartphone className="h-4 w-4" />, width: 320, height: 320 },
    { name: intl.formatMessage({ id: 'imageCropper.presets.custom' }), size: 'Free', aspect: undefined, icon: <CropIcon className="h-4 w-4" />, width: 400, height: 400 }
  ]

  const filterPresets = [
    { name: intl.formatMessage({ id: 'imageCropper.filters.none' }), value: 'none', filter: '' },
    { name: intl.formatMessage({ id: 'imageCropper.filters.vintage' }), value: 'vintage', filter: 'sepia(0.5) contrast(1.2) brightness(1.1)' },
    { name: intl.formatMessage({ id: 'imageCropper.filters.bw' }), value: 'bw', filter: 'grayscale(1) contrast(1.1)' },
    { name: intl.formatMessage({ id: 'imageCropper.filters.warm' }), value: 'warm', filter: 'sepia(0.3) saturate(1.3) brightness(1.05)' },
    { name: intl.formatMessage({ id: 'imageCropper.filters.cool' }), value: 'cool', filter: 'hue-rotate(180deg) saturate(1.2)' },
    { name: intl.formatMessage({ id: 'imageCropper.filters.highContrast' }), value: 'contrast', filter: 'contrast(1.5) brightness(1.1)' },
    { name: intl.formatMessage({ id: 'imageCropper.filters.soft' }), value: 'soft', filter: 'blur(0.5px) brightness(1.1) contrast(0.9)' }
  ]

  const exportSizes = {
    original: { name: intl.formatMessage({ id: 'imageCropper.export.originalSize' }), multiplier: 1 },
    small: { name: intl.formatMessage({ id: 'imageCropper.export.small' }), multiplier: 0.5 },
    medium: { name: intl.formatMessage({ id: 'imageCropper.export.medium' }), multiplier: 0.75 },
    large: { name: intl.formatMessage({ id: 'imageCropper.export.large' }), multiplier: 1.5 }
  }

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0])
    }
  }

  function processFile(file: File) {
    if (!file.type.startsWith('image/')) {
      alert(intl.formatMessage({ id: 'imageCropper.upload.selectImageFile' }))
      return
    }
    
    setIsProcessing(true)
    setCrop(undefined) // Makes crop preview update between images.
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      setImgSrc(reader.result?.toString() || '')
      setIsProcessing(false)
    })
    reader.readAsDataURL(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      processFile(files[0])
    }
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'd':
            e.preventDefault()
            if (completedCrop) {
              onDownloadCropClick()
            }
            break
          case 'r':
            e.preventDefault()
            handleReset()
            break
          case 'c':
            e.preventDefault()
            setIsCircular(!isCircular)
            break
          case '?':
            e.preventDefault()
            setShowKeyboardShortcuts(!showKeyboardShortcuts)
            break
        }
      }
      
      if (e.key === 'Escape') {
        setShowKeyboardShortcuts(false)
        setShowAdvancedControls(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [completedCrop, isCircular, showKeyboardShortcuts])

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget
      setCrop(centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          aspect,
          width,
          height,
        ),
        width,
        height,
      ))
    }
  }

  function onDownloadCropClick() {
    if (!previewCanvasRef.current) {
      throw new Error(intl.formatMessage({ id: 'imageCropper.errors.cropCanvasNotExist' }))
    }

    setIsDownloading(true)
    setDownloadSuccess(false)

    // 创建新的canvas来应用导出设置
    const exportCanvas = document.createElement('canvas')
    const exportCtx = exportCanvas.getContext('2d')
    
    if (!exportCtx) {
      setIsDownloading(false)
      throw new Error(intl.formatMessage({ id: 'imageCropper.errors.failedToCreateExportContext' }))
    }

    const originalCanvas = previewCanvasRef.current
    const multiplier = exportSizes[exportSize].multiplier
    
    // 使用目标尺寸而不是原始裁剪尺寸
    const finalWidth = targetWidth * multiplier
    const finalHeight = targetHeight * multiplier
    
    exportCanvas.width = finalWidth
    exportCanvas.height = finalHeight
    
    exportCtx.imageSmoothingEnabled = true
    exportCtx.imageSmoothingQuality = 'high'
    
    // 如果是圆形裁剪，先创建圆形路径
    if (isCircular) {
      const radius = Math.min(finalWidth, finalHeight) / 2
      const centerX = finalWidth / 2
      const centerY = finalHeight / 2
      
      exportCtx.save()
      exportCtx.beginPath()
      exportCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
      exportCtx.clip()
    }
    
    exportCtx.drawImage(originalCanvas, 0, 0, finalWidth, finalHeight)

    // 如果是圆形裁剪，恢复上下文
    if (isCircular) {
      exportCtx.restore()
    }

    const mimeType = exportFormat === 'jpeg' ? 'image/jpeg' : 
                     exportFormat === 'webp' ? 'image/webp' : 'image/png'
    const quality = exportFormat === 'png' ? undefined : exportQuality

    exportCanvas.toBlob((blob) => {
      if (!blob) {
        setIsDownloading(false)
        throw new Error(intl.formatMessage({ id: 'imageCropper.errors.failedToCreateBlob' }))
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
      blobUrlRef.current = URL.createObjectURL(blob)

      if (hiddenAnchorRef.current) {
        const fileName = `avatar.${exportFormat}`
        hiddenAnchorRef.current.href = blobUrlRef.current
        hiddenAnchorRef.current.download = fileName
        hiddenAnchorRef.current.click()
      }

      setIsDownloading(false)
      setDownloadSuccess(true)
      setTimeout(() => setDownloadSuccess(false), 3000)

      if (onCropComplete) {
        onCropComplete(blobUrlRef.current)
      }
    }, mimeType, quality)
  }

  const canvasPreview = useCallback(
    async (
      image: HTMLImageElement,
      canvas: HTMLCanvasElement,
      crop: PixelCrop,
      scale = 1,
      rotate = 0,
    ) => {
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        throw new Error(intl.formatMessage({ id: 'imageCropper.errors.no2dContext' }))
      }

      const scaleX = image.naturalWidth / image.width
      const scaleY = image.naturalHeight / image.height
      const pixelRatio = window.devicePixelRatio

      canvas.width = Math.floor(crop.width * scaleX * pixelRatio)
      canvas.height = Math.floor(crop.height * scaleY * pixelRatio)

      ctx.scale(pixelRatio, pixelRatio)
      ctx.imageSmoothingQuality = 'high'

      const cropX = crop.x * scaleX
      const cropY = crop.y * scaleY

      const rotateRads = rotate * (Math.PI / 180)
      const centerX = image.naturalWidth / 2
      const centerY = image.naturalHeight / 2

      // 如果是圆形裁剪，先创建圆形路径
      if (isCircular) {
        const radius = Math.min(crop.width * scaleX, crop.height * scaleY) / 2
        const circleX = (crop.width * scaleX) / 2
        const circleY = (crop.height * scaleY) / 2
        
        ctx.save()
        ctx.beginPath()
        ctx.arc(circleX / pixelRatio, circleY / pixelRatio, radius / pixelRatio, 0, 2 * Math.PI)
        ctx.clip()
      }

      ctx.save()

      ctx.translate(-cropX, -cropY)
      ctx.translate(centerX, centerY)
      ctx.rotate(rotateRads)
      ctx.scale(scale, scale)
      ctx.translate(-centerX, -centerY)
      
      // 应用滤镜效果
      if (selectedFilter !== 'none') {
        const filterStyle = filterPresets.find(f => f.value === selectedFilter)?.filter || ''
        ctx.filter = filterStyle
      } else {
        // 应用手动调整的滤镜
        ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px)`
      }
      
      ctx.drawImage(
        image,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
      )

      ctx.restore()
      
      // 如果是圆形裁剪，恢复裁剪路径
      if (isCircular) {
        ctx.restore()
      }
    },
    [isCircular, selectedFilter, filterPresets, brightness, contrast, saturation, blur, intl],
  )

  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        scale,
        rotate,
      )
    }
  }, [completedCrop, scale, rotate, canvasPreview])

  const handlePresetChange = (preset: PresetSize) => {
    setSelectedPreset(preset.name)
    setAspect(preset.aspect)
    setIsCircular(preset.name === 'Circular')
    setTargetWidth(preset.width)
    setTargetHeight(preset.height)
    
    if (imgRef.current && preset.aspect) {
      const { width, height } = imgRef.current
      setCrop(centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          preset.aspect,
          width,
          height,
        ),
        width,
        height,
      ))
    }
  }

  const handleRotate = () => {
    setRotate((prev) => (prev + 90) % 360)
  }

  const handleReset = () => {
    setScale(1)
    setRotate(0)
    setCrop(undefined)
    setCompletedCrop(undefined)
    setBrightness(100)
    setContrast(100)
    setSaturation(100)
    setBlur(0)
    setSelectedFilter('none')
    setExportFormat('png')
    setExportQuality(0.9)
    setExportSize('original')
    setShowAdvancedControls(false)
  }

  const getImageStyle = () => {
    const customFilter = selectedFilter === 'none' 
      ? `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px)`
      : filterPresets.find(f => f.value === selectedFilter)?.filter || ''
    
    return {
      transform: `scale(${scale}) rotate(${rotate}deg)`,
      filter: customFilter
    }
  }

  const handleFilterChange = (filterValue: string) => {
    setSelectedFilter(filterValue)
    if (filterValue !== 'none') {
      // Reset manual controls when applying a preset filter
      setBrightness(100)
      setContrast(100)
      setSaturation(100)
      setBlur(0)
    }
  }

  const downloadMultipleSizes = () => {
    const sizes = [128, 256, 512]
    sizes.forEach((size, index) => {
      setTimeout(() => {
        if (previewCanvasRef.current && completedCrop) {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          if (!ctx || !imgRef.current) return

          canvas.width = size
          canvas.height = size

          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'

          // 如果是圆形裁剪，先创建圆形路径
          if (isCircular) {
            const radius = size / 2
            const centerX = size / 2
            const centerY = size / 2
            
            ctx.save()
            ctx.beginPath()
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
            ctx.clip()
          }

          ctx.drawImage(
            previewCanvasRef.current,
            0, 0, size, size
          )

          // 如果是圆形裁剪，恢复上下文
          if (isCircular) {
            ctx.restore()
          }

          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `avatar-${size}x${size}.png`
              a.click()
              URL.revokeObjectURL(url)
            }
          })
        }
      }, index * 500) // Stagger downloads
    })
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-4 sm:p-6 bg-white rounded-2xl shadow-xl">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{intl.formatMessage({ id: 'imageCropper.title' })}</h2>
          <p className="text-gray-600">{intl.formatMessage({ id: 'imageCropper.description' })}</p>
        </div>
        <motion.button
          onClick={() => setShowKeyboardShortcuts(!showKeyboardShortcuts)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title={intl.formatMessage({ id: 'imageCropper.shortcuts.showShortcuts' }) + " (Ctrl+?)"}
        >
          <span className="text-sm font-mono">?</span>
        </motion.button>
      </div>

      {/* File Upload */}
      <div className="mb-6">
        <label 
          className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
            isDragOver 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
          } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            {isProcessing ? (
              <RefreshCw className="w-8 h-8 mb-2 text-primary-500 animate-spin" />
            ) : (
              <Upload className={`w-8 h-8 mb-2 ${isDragOver ? 'text-primary-500' : 'text-gray-500'}`} />
            )}
            <p className="mb-2 text-sm text-gray-500">
              {isProcessing ? (
                 <span className="font-semibold">{intl.formatMessage({ id: 'imageCropper.upload.processing' })}</span>
               ) : (
                 <>
                   <span className="font-semibold">{intl.formatMessage({ id: 'imageCropper.upload.clickToUpload' })}</span> {intl.formatMessage({ id: 'imageCropper.upload.dragHere' })}
                 </>
               )}
             </p>
             <p className="text-xs text-gray-500">{intl.formatMessage({ id: 'imageCropper.upload.supportedFormats' })}</p>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={onSelectFile}
            className="hidden"
            disabled={isProcessing}
          />
        </label>
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showKeyboardShortcuts && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowKeyboardShortcuts(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4 text-center">{intl.formatMessage({ id: 'imageCropper.shortcuts.title' })}</h3>
             <div className="space-y-3">
               <div className="flex justify-between items-center">
                 <span className="text-gray-600 text-sm sm:text-base">{intl.formatMessage({ id: 'imageCropper.shortcuts.downloadImage' })}</span>
                 <kbd className="px-2 py-1 bg-gray-100 rounded text-xs sm:text-sm">Ctrl + D</kbd>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-gray-600 text-sm sm:text-base">{intl.formatMessage({ id: 'imageCropper.shortcuts.resetCrop' })}</span>
                 <kbd className="px-2 py-1 bg-gray-100 rounded text-xs sm:text-sm">Ctrl + R</kbd>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-gray-600 text-sm sm:text-base">{intl.formatMessage({ id: 'imageCropper.shortcuts.toggleCircle' })}</span>
                 <kbd className="px-2 py-1 bg-gray-100 rounded text-xs sm:text-sm">Ctrl + C</kbd>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-gray-600 text-sm sm:text-base">{intl.formatMessage({ id: 'imageCropper.shortcuts.showShortcuts' })}</span>
                 <kbd className="px-2 py-1 bg-gray-100 rounded text-xs sm:text-sm">Ctrl + ?</kbd>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-gray-600 text-sm sm:text-base">{intl.formatMessage({ id: 'imageCropper.shortcuts.closeModal' })}</span>
                 <kbd className="px-2 py-1 bg-gray-100 rounded text-xs sm:text-sm">Esc</kbd>
               </div>
             </div>
          </motion.div>
        </motion.div>
      )}

      {/* Preset Sizes */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">{intl.formatMessage({ id: 'imageCropper.presets.title' })}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {presetSizes.map((preset) => (
            <motion.button
              key={preset.name}
              onClick={() => handlePresetChange(preset)}
              className={`p-3 rounded-lg border-2 transition-all ${
                selectedPreset === preset.name
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex flex-col items-center space-y-1">
                {preset.icon}
                <span className="text-xs font-medium">{preset.name}</span>
                <span className="text-xs text-gray-500">{preset.size}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Controls */}
      {imgSrc && (
        <div className="mb-6 space-y-4">
          {/* Basic Controls */}
          <div className="space-y-4">
            {/* Scale Control */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'imageCropper.controls.scale' })}</label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="flex-1 max-w-32"
              />
              <span className="text-sm text-gray-600 min-w-[3rem]">{scale.toFixed(1)}x</span>
            </div>
            
            {/* Action Buttons */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <motion.button
                onClick={handleRotate}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <RotateCw className="h-4 w-4" />
                <span>{intl.formatMessage({ id: 'imageCropper.controls.rotate' })}</span>
              </motion.button>
              
              <motion.button
                onClick={handleReset}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <RefreshCw className="h-4 w-4" />
                <span>{intl.formatMessage({ id: 'imageCropper.controls.reset' })}</span>
              </motion.button>
              
              <motion.button
                onClick={() => setShowAdvancedControls(!showAdvancedControls)}
                className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  showAdvancedControls 
                    ? 'bg-purple-500 text-white hover:bg-purple-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Sliders className="h-4 w-4" />
                <span>{intl.formatMessage({ id: 'imageCropper.controls.advancedControls' })}</span>
              </motion.button>
            </div>
          </div>

          {/* Advanced Controls */}
          {showAdvancedControls && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 p-4 bg-gray-50 rounded-lg"
            >
              {/* Brightness */}
              <div className="flex items-center space-x-2">
                <Sun className="h-4 w-4 text-yellow-500" />
                <label className="text-sm font-medium text-gray-700 min-w-[4rem]">{intl.formatMessage({ id: 'imageCropper.controls.brightness' })}</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={brightness}
                  onChange={(e) => setBrightness(Number(e.target.value))}
                  className="flex-1 max-w-32"
                />
                <span className="text-sm text-gray-600 min-w-[3rem]">{brightness}%</span>
              </div>
              
              {/* Contrast */}
              <div className="flex items-center space-x-2">
                <Contrast className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-medium text-gray-700 min-w-[4rem]">{intl.formatMessage({ id: 'imageCropper.controls.contrast' })}</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={contrast}
                  onChange={(e) => setContrast(Number(e.target.value))}
                  className="flex-1 max-w-32"
                />
                <span className="text-sm text-gray-600 min-w-[3rem]">{contrast}%</span>
              </div>
              
              {/* Saturation */}
              <div className="flex items-center space-x-2">
                <Palette className="h-4 w-4 text-pink-500" />
                <label className="text-sm font-medium text-gray-700 min-w-[4rem]">{intl.formatMessage({ id: 'imageCropper.controls.saturation' })}</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={saturation}
                  onChange={(e) => setSaturation(Number(e.target.value))}
                  className="flex-1 max-w-32"
                />
                <span className="text-sm text-gray-600 min-w-[3rem]">{saturation}%</span>
              </div>
              
              {/* Blur */}
              <div className="flex items-center space-x-2">
                <Eye className="h-4 w-4 text-blue-500" />
                <label className="text-sm font-medium text-gray-700 min-w-[4rem]">{intl.formatMessage({ id: 'imageCropper.controls.blur' })}</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.1"
                  value={blur}
                  onChange={(e) => setBlur(Number(e.target.value))}
                  className="flex-1 max-w-32"
                />
                <span className="text-sm text-gray-600 min-w-[3rem]">{blur.toFixed(1)}px</span>
              </div>
              
              {/* Filter Presets */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'imageCropper.controls.filters' })}</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {filterPresets.map((filter) => (
                    <motion.button
                      key={filter.value}
                      onClick={() => handleFilterChange(filter.value)}
                      className={`px-3 py-2 text-xs rounded-lg transition-all ${
                        selectedFilter === filter.value
                          ? 'bg-purple-500 text-white'
                          : 'bg-white hover:bg-gray-100 text-gray-700 border'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {filter.name}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Image and Crop Area */}
      {imgSrc && (
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Original Image with Crop */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Original Image</h4>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                circularCrop={isCircular}
                className="max-w-full"
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  style={getImageStyle()}
                  onLoad={onImageLoad}
                  className="max-w-full h-auto"
                />
              </ReactCrop>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Preview</h4>
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 flex items-center justify-center min-h-[200px]">
              {completedCrop ? (
                <canvas
                  ref={previewCanvasRef}
                  className={`max-w-full h-auto border ${isCircular ? 'rounded-full' : 'rounded-lg'} shadow-lg`}
                />
              ) : (
                <div className="text-gray-400 text-center">
                  <FileImage className="h-12 w-12 mx-auto mb-2" />
                  <p>Crop preview will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Export Settings and Download */}
      {completedCrop && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{intl.formatMessage({ id: 'imageCropper.controls.exportSettings' })}</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Format Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'imageCropper.export.format' })}</label>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'png' | 'jpeg' | 'webp')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="png">PNG</option>
                <option value="jpeg">JPEG</option>
                <option value="webp">WebP</option>
              </select>
            </div>
            
            {/* Quality (for JPEG/WebP) */}
            {exportFormat !== 'png' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'imageCropper.export.quality' })}</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={exportQuality}
                    onChange={(e) => setExportQuality(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-600 min-w-[3rem]">{Math.round(exportQuality * 100)}%</span>
                </div>
              </div>
            )}
            
            {/* Size Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{intl.formatMessage({ id: 'imageCropper.export.size' })}</label>
              <select
                value={exportSize}
                onChange={(e) => setExportSize(e.target.value as 'original' | 'small' | 'medium' | 'large')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {Object.entries(exportSizes).map(([key, { name }]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Download Buttons */}
          <div className="flex flex-wrap gap-3">
            <motion.button
              onClick={onDownloadCropClick}
              disabled={isDownloading}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isDownloading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : downloadSuccess
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-primary-500 hover:bg-primary-600'
              } text-white`}
              whileHover={!isDownloading ? { scale: 1.02 } : {}}
              whileTap={!isDownloading ? { scale: 0.98 } : {}}
            >
              {isDownloading ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>{intl.formatMessage({ id: 'imageCropper.export.downloading' })}</span>
                </>
              ) : downloadSuccess ? (
                <>
                  <Download className="h-5 w-5" />
                  <span>{intl.formatMessage({ id: 'imageCropper.export.downloadSuccess' })}</span>
                </>
              ) : (
                <>
                  <Download className="h-5 w-5" />
                  <span>{intl.formatMessage({ id: 'imageCropper.export.download' })}</span>
                </>
              )}
            </motion.button>
            
            <motion.button
              onClick={downloadMultipleSizes}
              className="flex items-center space-x-2 px-6 py-3 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Maximize className="h-5 w-5" />
              <span>{intl.formatMessage({ id: 'imageCropper.export.downloadMultiple' })}</span>
            </motion.button>
          </div>
        </div>
      )}

      {/* Hidden anchor for downloads */}
      <a
        ref={hiddenAnchorRef}
        download
        style={{
          position: 'absolute',
          top: '-200vh',
          visibility: 'hidden',
        }}
      >
        Hidden download
      </a>
    </div>
  )
}

export default ImageCropper